import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/AboutDetailed.css';

const AboutDetailed = () => {
    const researchAreas = [
        {
            title: "Advanced Formulation Technology",
            description: "Our R&D team develops cutting-edge lubricant formulations using the latest synthetic base oils and additive technologies. We focus on creating products that exceed industry standards while maintaining environmental sustainability.",
            icon: "🧪",
            features: ["Synthetic base oil development", "Additive package optimization", "Performance testing protocols", "Environmental impact assessment"]
        },
        {
            title: "Performance Testing & Validation",
            description: "State-of-the-art testing facilities ensure every product meets or exceeds international standards. Our testing protocols include engine dynamometer tests, field trials, and long-term performance studies.",
            icon: "🔬",
            features: ["Engine dynamometer testing", "Field trial validation", "Long-term performance studies", "Quality control automation"]
        },
        {
            title: "Sustainability Research",
            description: "Committed to developing eco-friendly lubricant solutions that reduce environmental impact while maintaining superior performance. Our research focuses on biodegradable formulations and renewable resources.",
            icon: "🌱",
            features: ["Biodegradable formulations", "Renewable resource utilization", "Carbon footprint reduction", "Circular economy principles"]
        }
    ];

    const globalPresence = [
        {
            region: "North India",
            states: ["Delhi", "Haryana", "Punjab", "Uttar Pradesh", "Uttarakhand"],
            facilities: "Manufacturing Hub & R&D Center",
            icon: "🏔️"
        },
        {
            region: "South India",
            states: ["Karnataka", "Tamil Nadu", "Kerala", "Andhra Pradesh", "Telangana"],
            facilities: "Distribution Centers & Service Centers",
            icon: "🌴"
        },
        {
            region: "East India",
            states: ["West Bengal", "Bihar", "Jharkhand", "Odisha"],
            facilities: "Regional Offices & Dealership Network",
            icon: "🌅"
        },
        {
            region: "West India",
            states: ["Maharashtra", "Gujarat", "Rajasthan", "Madhya Pradesh"],
            facilities: "Manufacturing Units & Distribution",
            icon: "🏜️"
        }
    ];

    const qualityStandards = [
        {
            standard: "ISO 9001:2015",
            description: "Quality Management System certification ensuring consistent product quality and customer satisfaction",
            scope: "Product development, manufacturing, and customer service",
            icon: "🏆"
        },
        {
            standard: "API Certification",
            description: "American Petroleum Institute certification for engine oil performance and quality standards",
            scope: "Engine oil formulations and performance testing",
            icon: "⚡"
        },
        {
            standard: "BIS Certification",
            description: "Bureau of Indian Standards certification for domestic market compliance",
            scope: "All product categories and manufacturing processes",
            icon: "🇮🇳"
        },
        {
            standard: "OEM Approvals",
            description: "Original Equipment Manufacturer approvals for compatibility with major vehicle brands",
            scope: "Engine oil compatibility and performance",
            icon: "🔧"
        }
    ];

    const innovationProjects = [
        {
            title: "Smart Lubrication Systems",
            description: "Development of IoT-enabled lubrication monitoring systems for predictive maintenance",
            status: "In Development",
            timeline: "2024-2025",
            icon: "🤖"
        },
        {
            title: "Bio-Based Lubricants",
            description: "Research into sustainable lubricant formulations using renewable plant-based materials",
            status: "Research Phase",
            timeline: "2024-2026",
            icon: "🌿"
        },
        {
            title: "Advanced Additive Technology",
            description: "Development of next-generation additive packages for extended oil life and performance",
            status: "Testing Phase",
            timeline: "2024-2025",
            icon: "⚗️"
        },
        {
            title: "Digital Twin Technology",
            description: "Implementation of digital twin technology for manufacturing process optimization",
            status: "Planning Phase",
            timeline: "2025-2026",
            icon: "💻"
        }
    ];

    return (
        <div className="about-detailed">
            {/* Hero Section */}
            <section className="detailed-hero">
                <div className="container">
                    <div className="hero-content">
                        <h1>MapLube: A Comprehensive Overview</h1>
                        <p>Discover the complete story behind India's leading lubricant manufacturer</p>
                        <Link to="/about" className="btn btn-outline">← Back to About</Link>
                    </div>
                </div>
            </section>

            {/* Company Evolution */}
            <section className="company-evolution">
                <div className="container">
                    <h2>Company Evolution & Growth</h2>
                    <div className="evolution-content">
                        <div className="evolution-text">
                            <h3>The Early Years (2010-2015)</h3>
                            <p>MapLube began as a small-scale lubricant manufacturer in North India, focusing on serving local automotive workshops and small fleet operators. During this period, we established our core manufacturing processes and quality control systems.</p>

                            <h3>Expansion Phase (2015-2020)</h3>
                            <p>The company experienced significant growth, expanding operations to cover major Indian states. We invested heavily in modern manufacturing equipment and obtained crucial certifications that positioned us as a trusted industry player.</p>

                            <h3>Innovation Era (2020-Present)</h3>
                            <p>MapLube entered a new phase focused on innovation and sustainability. We launched our state-of-the-art R&D center, expanded our product portfolio, and strengthened our position as a market leader in the Indian lubricant industry.</p>
                        </div>
                        <div className="evolution-stats">
                            <div className="stat-card">
                                <div className="stat-number">14+</div>
                                <div className="stat-label">Years of Excellence</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-number">50+</div>
                                <div className="stat-label">Product Variants</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-number">1000+</div>
                                <div className="stat-label">Authorized Dealers</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-number">1M+</div>
                                <div className="stat-label">Happy Customers</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Research & Development */}
            <section className="research-development">
                <div className="container">
                    <h2>Research & Development Excellence</h2>
                    <p className="section-description">Our commitment to innovation drives us to continuously improve and develop cutting-edge lubricant solutions</p>
                    <div className="research-grid">
                        {researchAreas.map((area, index) => (
                            <div key={index} className="research-card">
                                <div className="research-icon">{area.icon}</div>
                                <h3>{area.title}</h3>
                                <p>{area.description}</p>
                                <ul className="research-features">
                                    {area.features.map((feature, idx) => (
                                        <li key={idx}>{feature}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Global Presence */}
            <section className="global-presence">
                <div className="container">
                    <h2>National Presence & Infrastructure</h2>
                    <p className="section-description">MapLube's extensive network spans across India, ensuring accessibility and support for customers nationwide</p>
                    <div className="presence-grid">
                        {globalPresence.map((region, index) => (
                            <div key={index} className="presence-card">
                                <div className="presence-icon">{region.icon}</div>
                                <h3>{region.region}</h3>
                                <div className="region-states">
                                    <strong>States Covered:</strong>
                                    <p>{region.states.join(", ")}</p>
                                </div>
                                <div className="region-facilities">
                                    <strong>Facilities:</strong>
                                    <p>{region.facilities}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quality Standards */}
            <section className="quality-standards">
                <div className="container">
                    <h2>Quality Standards & Certifications</h2>
                    <p className="section-description">Our commitment to quality is reflected in our comprehensive certification portfolio and adherence to international standards</p>
                    <div className="standards-grid">
                        {qualityStandards.map((standard, index) => (
                            <div key={index} className="standard-card">
                                <div className="standard-icon">{standard.icon}</div>
                                <h3>{standard.standard}</h3>
                                <p>{standard.description}</p>
                                <div className="standard-scope">
                                    <strong>Scope:</strong> {standard.scope}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Innovation Projects */}
            <section className="innovation-projects">
                <div className="container">
                    <h2>Innovation Projects & Future Vision</h2>
                    <p className="section-description">MapLube is committed to staying at the forefront of lubricant technology through continuous innovation and research</p>
                    <div className="projects-grid">
                        {innovationProjects.map((project, index) => (
                            <div key={index} className="project-card">
                                <div className="project-icon">{project.icon}</div>
                                <h3>{project.title}</h3>
                                <p>{project.description}</p>
                                <div className="project-status">
                                    <span className={`status-badge ${project.status.toLowerCase().replace(' ', '-')}`}>
                                        {project.status}
                                    </span>
                                    <span className="project-timeline">{project.timeline}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Environmental Commitment */}
            <section className="environmental-commitment">
                <div className="container">
                    <h2>Environmental Commitment & Sustainability</h2>
                    <div className="environmental-content">
                        <div className="environmental-text">
                            <h3>Our Environmental Mission</h3>
                            <p>MapLube is committed to reducing our environmental footprint while maintaining the highest standards of product performance. We implement sustainable practices throughout our operations, from raw material sourcing to product disposal.</p>

                            <h3>Sustainable Manufacturing</h3>
                            <p>Our manufacturing facilities utilize energy-efficient processes, waste reduction systems, and environmentally responsible packaging solutions. We continuously invest in technologies that minimize environmental impact.</p>

                            <h3>Product Sustainability</h3>
                            <p>We develop lubricants that extend equipment life, reduce maintenance frequency, and contribute to overall operational efficiency, thereby supporting our customers' sustainability goals.</p>
                        </div>
                        <div className="environmental-metrics">
                            <div className="metric-card">
                                <div className="metric-number">25%</div>
                                <div className="metric-label">Reduction in Carbon Footprint</div>
                            </div>
                            <div className="metric-card">
                                <div className="metric-number">90%</div>
                                <div className="metric-label">Recyclable Packaging</div>
                            </div>
                            <div className="metric-card">
                                <div className="metric-number">100%</div>
                                <div className="metric-label">ISO 14001 Compliant</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="detailed-cta">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to Experience MapLube Excellence?</h2>
                        <p>Join thousands of satisfied customers who trust MapLube for their lubrication needs</p>
                        <div className="cta-buttons">
                            <Link to="/products" className="btn btn-primary">Explore Products</Link>
                            <Link to="/contact" className="btn btn-secondary">Get in Touch</Link>
                            <Link to="/dealership" className="btn btn-outline">Become a Dealer</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutDetailed;
